// src/components/ColorStatsPie.js
import React, { useEffect, useState } from 'react';
import { firestore, doc, onSnapshot } from '../firebase-config';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import './ColorStatsPie.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const ColorStatsPie = ({ playerData, mode = 'total', title }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (mode === 'total') {
      return loadTotalColorStats();
    } else {
      return loadCoverageColorStats();
    }
  }, [playerData.uuid, mode]);

  const loadTotalColorStats = () => {
    const colorStatsRef = doc(firestore, 'players', playerData.uuid, 'colourStats', 'stats');
    
    const unsubscribe = onSnapshot(colorStatsRef, (colorStatsDoc) => {
      if (colorStatsDoc.exists()) {
        const data = colorStatsDoc.data();
        formatChartData(data);
      } else {
        setChartData(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading total color stats:', error);
      setLoading(false);
    });

    return unsubscribe;
  };

  const loadCoverageColorStats = () => {
    //Read from statsOwned instead of counting ownedBlocks
    const colorStatsOwnedRef = doc(firestore, 'players', playerData.uuid, 'colourStats', 'statsOwned');

    const unsubscribe = onSnapshot(colorStatsOwnedRef, (colorStatsDoc) => {
      if (colorStatsDoc.exists()) {
        const data = colorStatsDoc.data();
        formatChartData(data);
      } else {
        setChartData(null);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading coverage color stats:', error);
      setLoading(false);
    });

    return unsubscribe;
  };

  const formatChartData = (data) => {
    const colorMap = {
      'WHITE': '#FFFFFF',
      'BLACK': '#080A0F',
      'BLUE': '#2D2F8F',
      'PINK': '#BE4B7A',
      'BROWN': '#603A1E',
      'CYAN': '#158690',
      'GREEN': '#495B24',
      'GRAY': '#373A3E',
      'LIGHT_BLUE': '#2AABCC',
      'LIGHT_GRAY': '#7D7D73',
      'LIME': '#5EA818',
      'MAGENTA': '#A8308C',
      'ORANGE': '#E06100',
      'RED': '#8E2020',
      'PURPLE': '#5F1F8F',
      'YELLOW': '#F0AF15'
    };

    const labels = [];
    const values = [];
    const colors = [];

    Object.entries(data).forEach(([color, count]) => {
      if (count > 0) {
        labels.push(color);
        values.push(count);
        colors.push(colorMap[color] || '#CCCCCC');
      }
    });

    setChartData({
      labels,
      datasets: [{
        data: values,
        backgroundColor: colors,
        borderColor: '#1a1a1a',
        borderWidth: 2
      }]
    });
  };

  if (loading) {
    return (
      <div className="color-stats-pie card">
        <h3>{title || 'Color Distribution'}</h3>
        <div className="loading">Loading color stats...</div>
      </div>
    );
  }

  if (!chartData || chartData.labels.length === 0) {
    return (
      <div className="color-stats-pie card">
        <h3>{title || 'Color Distribution'}</h3>
        <p className="no-data">No color data available</p>
      </div>
    );
  }

  return (
    <div className="color-stats-pie card">
      <h3>{title || 'Color Distribution'}</h3>
      <div className="chart-container">
        <Doughnut 
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  color: '#e0e0e0',
                  padding: 10,
                  font: { size: 11 }
                }
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const label = context.label || '';
                    const value = context.parsed || 0;
                    const total = context.dataset.data.reduce((a, b) => a + b, 0);
                    const percentage = ((value / total) * 100).toFixed(1);
                    return `${label}: ${value.toLocaleString()} (${percentage}%)`;
                  }
                }
              }
            }
          }}
        />
      </div>
    </div>
  );
};

export default ColorStatsPie;